<template>
  <div class="flex flex-col">
    <div class="flex flex-col items-start gap-4 self-stretch">
      <div class="flex items-center gap-4 self-stretch">
        <UIButton id="backButton" class="pl-0" :quaternary="true" :text="false" @click="onBack">
          <ArrowLeftIcon class="h-5 w-5"></ArrowLeftIcon>
          {{ t("common.back") }}
        </UIButton>
        <UITextMdMedium>{{ t("common.payment.product.productSettings") }}</UITextMdMedium>
      </div>
      <div class="flex flex-1 flex-col items-start justify-between gap-3 self-stretch">
        <section class="flex flex-col gap-2">
          <UITextSmMedium class="text-gray-700">{{
            t("common.payment.product.chooseProductType")
          }}</UITextSmMedium>
          <div class="flex gap-3 self-stretch">
            <button
              :id="PRODUCT_TYPE.MAIN_PRODUCT.value"
              type="button"
              class="product-type-card flex flex-1 flex-col items-start gap-2 rounded-lg border-2 border-solid p-3 text-left shadow-none transition-colors"
              :class="[
                product?.type === PRODUCT_TYPE.MAIN_PRODUCT.value
                  ? 'product-type-card--selected border-[#155EEF] bg-[#EFF4FF]'
                  : 'border-gray-200 bg-white hover:border-gray-300',
              ]"
              :disabled="isMainProductDisabled"
              :aria-label="PRODUCT_TYPE.MAIN_PRODUCT.label"
              @click="
                !isMainProductDisabled &&
                  updateProductTypeFromValue(PRODUCT_TYPE.MAIN_PRODUCT.value)
              "
            >
              <div
                class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                :class="'bg-[#D1E0FF]'"
              >
                <img
                  :src="mainProductIcon"
                  class="h-9 w-9"
                  alt=""
                  aria-hidden="true"
                />
              </div>
              <UITextSmMedium
                :class="
                  product?.type === PRODUCT_TYPE.MAIN_PRODUCT.value
                    ? 'text-[#155EEF]'
                    : 'text-gray-700'
                "
              >
                {{ PRODUCT_TYPE.MAIN_PRODUCT.label }}
              </UITextSmMedium>
              <UITextSmRegular class="text-gray-500">{{
                t("common.payment.product.primaryOffer")
              }}</UITextSmRegular>
            </button>
            <UITooltip trigger="hover" placement="top" width="300">
              <template #trigger>
                <button
                  :id="PRODUCT_TYPE.BUMP_PRODUCT.value"
                  type="button"
                  class="product-type-card flex flex-1 flex-col items-start gap-2 rounded-lg border-2 border-solid p-3 text-left shadow-none transition-colors"
                  :class="[
                    product?.type === PRODUCT_TYPE.BUMP_PRODUCT.value
                      ? 'product-type-card--selected border-[#155EEF] bg-[#EFF4FF]'
                      : 'border-gray-200 bg-white hover:border-gray-300',
                  ]"
                  :disabled="isBumpDisabled"
                  :aria-label="PRODUCT_TYPE.BUMP_PRODUCT.label"
                  @click="
                    !isBumpDisabled && updateProductTypeFromValue(PRODUCT_TYPE.BUMP_PRODUCT.value)
                  "
                >
                  <div
                    class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                    :class="'bg-[#D1E0FF]'"
                  >
                    <img
                      :src="bumpProductIcon"
                      class="h-9 w-9"
                      alt=""
                      aria-hidden="true"
                    />
                  </div>
                  <UITextSmMedium
                    :class="
                      product?.type === PRODUCT_TYPE.BUMP_PRODUCT.value
                        ? 'text-[#155EEF]'
                        : 'text-gray-700'
                    "
                  >
                    {{ PRODUCT_TYPE.BUMP_PRODUCT.label }}
                  </UITextSmMedium>
                  <UITextSmRegular class="text-gray-500">{{
                    t("common.payment.product.checkoutAddOn")
                  }}</UITextSmRegular>
                </button>
              </template>
              <template v-if="isBumpDisabled" #default>
                {{ t("common.payment.error.addMainProductFirst") }}
              </template>
              <template v-else #default>
                {{ t("common.payment.bumProduct.bumpProductTooltip") }}
              </template>
            </UITooltip>
          </div>
        </section>
        <div class="flex flex-col items-start justify-between gap-3 self-stretch">
          <div class="flex space-x-3">
            <UITextSmMedium class="text-gray-700">{{
              t("common.payment.product.selectProduct")
            }}</UITextSmMedium>
          </div>
          <UISelect
            id="select-id"
            :value="product?.name"
            filterable
            :options="productList"
            :placeholder="t('common.payment.product.productSelectPlaceholder')"
            :reset-menu-on-options-change="false"
            :loading="isLoading"
            :disabled="!!currentProduct"
            clearable
            remote
            size="medium"
            @scroll="handleScroll"
            @update:value="updateProduct"
            @search="handleSearch"
          />
          <UITextSmRegular
            >{{
              currentProduct
                ? t("common.payment.product.editThisProduct")
                : t("common.payment.product.createProduct")
            }}
            <a class="cursor-pointer" @click="onProductEdit">{{
              t("common.payment.product.clickHere")
            }}</a>
          </UITextSmRegular>
        </div>
       
        <BumProductSettings
          v-if="product?.type === PRODUCT_TYPE.BUMP_PRODUCT.value"
          :product="product"
          :is-edit="!!currentProduct"
          @update:bum-product="updateBumProduct"
          @validate:bum-product-settings="validateBumProductSettings"
        />
        <div v-else class="flex flex-col items-start gap-4 self-stretch">
          <div
            v-if="product?.prices?.length"
            class="flex flex-col items-start justify-between gap-2 self-stretch"
          >
            <UITextSmMedium class="text-gray-700">{{
              t("common.payment.product.pricingOptions")
            }}</UITextSmMedium>
            <UISelect
              id="product-settings-pricing-options"
              :value="mainProductSettings.selectedPriceIds"
              :options="priceOptions"
              :placeholder="t('common.payment.product.pricingOptionsPlaceholder')"
              multiple
              size="medium"
              @update:value="onPricingOptionsUpdate"
            />
          </div>
          <div class="flex flex-col items-start justify-between gap-2 self-stretch">
            <div class="flex space-x-3">
              <UITextSmMedium>{{ t("common.payment.product.show") }}</UITextSmMedium>
            </div>
            <UICheckbox
              id="showDescription"
              :checked="mainProductSettings?.showDescription"
              @update:checked="onDescriptionUpdate"
            >
              <UITextSmMedium>{{
                t("common.payment.product.description")
              }}</UITextSmMedium></UICheckbox
            >
            <UICheckbox
              id="showImage"
              :checked="mainProductSettings?.showImage"
              @update:checked="onImageUpdate"
            >
              <UITextSmMedium>{{ t("common.payment.product.image") }}</UITextSmMedium></UICheckbox
            >
            <UICheckbox
              id="showQuantity"
              :checked="mainProductSettings?.showQuantity"
              @update:checked="onQuantityUpdate"
            >
              <UITextSmMedium>{{
                t("common.payment.product.quantity")
              }}</UITextSmMedium></UICheckbox
            >
          </div>
        </div>
      </div>
    </div>

    <div class="sticky bottom-0 mt-auto bg-white">
      <div class="flex w-full flex-col gap-2">
        <div class="border-button">
          <div class="flex w-full items-center justify-between gap-2 px-2">
            <UIButton id="productCancel" class="flex-1" size="medium" @click="onBack">
              {{ t("common.cancel") }}
            </UIButton>
            <UIButton
              id="addProduct"
              type="primary"
              class="flex-1"
              size="medium"
              @click="addProduct"
            >
              {{ t("common.payment.product.addProduct") }}
            </UIButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import bumpProductIcon from "@/assets/icons/bump-product-featured-icon.svg?url";
import mainProductIcon from "@/assets/icons/main-product-featured-icon.svg?url";
import { useNotify } from "@/composition/notification";
import config from "@/config";
import { PaymentServices } from "@/service/PaymentServices";
import { useAppStore } from "@/store/app";
import { BumProduct } from "@/types/product";
import { PRODUCT_TYPE } from "@/util/productConstants";
import { ArrowLeftIcon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UIButton,
  UICheckbox,
  UIForm,
  UISelect,
  UITextMdMedium,
  UITextSmMedium,
  UITextSmRegular,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import BumProductSettings from "./BumProductSettings.vue";

const { sendNotification } = useNotify();
const { t } = useI18n();
const store = useAppStore();
const props = defineProps({
  currentProduct: {
    type: Object,
    required: true,
  },
});
const emit = defineEmits(["onBack"]);
const product = ref(props.currentProduct);
const skip = ref(0);
const productList = ref(store.productList);
const isLoading = ref(false);
const totalProduct = ref(0);
const productSearch = ref("");
const bumProductSettingsFormRef = ref<InstanceType<typeof UIForm>>();
const getAllPriceIds = (prices: { _id: string }[] | undefined) =>
  prices?.map((p) => p._id) ?? [];

const mainProductSettings = ref({
  showDescription: props.currentProduct?.showDescription ?? true,
  showImage: props.currentProduct?.showImage ?? true,
  showQuantity: props.currentProduct?.showQuantity ?? true,
  selectedPriceIds:
    (props.currentProduct?.selectedPriceIds?.length ?? 0) > 0
      ? props.currentProduct.selectedPriceIds
      : getAllPriceIds(props.currentProduct?.prices),
});
const priceOptions = computed(() => {
  const prices = product.value?.prices;
  if (!prices?.length) return [];
  return prices.map((price: { _id: string; name: string }) => ({
    label: price.name,
    value: price._id,
  }));
});

const handleScroll = async (e) => {
  if (isLoading.value || !hasNextPage.value) return;

  const currentTarget = e.currentTarget as HTMLElement;
  const isScrolledToBottom =
    currentTarget.scrollTop + currentTarget.offsetHeight + 20 >= currentTarget.scrollHeight;

  if (!isScrolledToBottom) return;

  isLoading.value = true;
  try {
    const nextSkip = skip.value + 10;
    await handlePaginationAPICall(productSearch.value, nextSkip, 10);
    skip.value = nextSkip;
  } catch (error) {
    console.error("Error fetching paginated products:", error);
  } finally {
    isLoading.value = false;
  }
};

const handleSearch = async (search) => {
  productSearch.value = search;
  skip.value = 0;
  await handlePaginationAPICall(search, 0, 10, "replace");
};

const handlePaginationAPICall = async (search = "", skip, limit, type = "append") => {
  const response = await PaymentServices.getProductList({
    locationId: store.locationId,
    search,
    offset: skip,
    limit,
  });
  const products = response.data?.products ?? [];
  totalProduct.value = response.data.total?.[0]?.total ?? totalProduct.value;
  if (type === "append") {
    const existingProductIds = new Set(
      productList.value.map((item) => item.value?.productId)
    );
    const newProductOptions = products.reduce((options, el) => {
      const obj = {
        productId: el._id,
        amount: el.prices[0].amount,
        currency: el.prices[0].currency,
        showDescription: true,
        showImage: true,
        showQuantity: true,
        ...el,
      };
      if (!existingProductIds.has(obj.productId)) {
        existingProductIds.add(obj.productId);
        options.push({ label: obj.name, value: obj });
      }
      return options;
    }, []);
    productList.value = [...productList.value, ...newProductOptions];
    if (newProductOptions.length === 0) {
      totalProduct.value = productList.value.length;
    }
  } else {
    productList.value = products.map((el) => {
      const obj = {
        productId: el._id,
        amount: el.prices[0].amount,
        currency: el.prices[0].currency,
        showDescription: true,
        showImage: true,
        showQuantity: true,
        ...el,
      };
      return { label: obj.name, value: obj };
    });
  }
};

const onProductEdit = () => {
  if (props.currentProduct) {
    window
      .open(
        `https://${store.company.domain ? store.company.domain : config.GHL_DOMAIN}/v2/location/${
          store.locationId
        }/payments/products/edit/${product.value.productId}`,
        "_blank"
      )
      .focus();
  } else {
    window
      .open(
        `https://${store.company.domain ? store.company.domain : config.GHL_DOMAIN}/v2/location/${
          store.locationId
        }/payments/products`,
        "_blank"
      )
      .focus();
  }
};
const hasNextPage = computed(() => {
  return productList.value.length < totalProduct.value && skip.value < totalProduct.value;
});

onMounted(async () => {
  totalProduct.value = store.totalProduct;
});

const updateProduct = async (value) => {
  if (!value) {
    product.value = value;
    productSearch.value = "";
    skip.value = 0;
    await handlePaginationAPICall("", 0, 10, "replace");
    return;
  }

  product.value = value;
  product.value.type = PRODUCT_TYPE.MAIN_PRODUCT.value;
  product.value.priceId = value?.prices?.[0]?._id;
  mainProductSettings.value.selectedPriceIds = getAllPriceIds(value?.prices);
  store.anyUnsavedChanges = true;
};

const validateBumProductSettings = (e: InstanceType<typeof UIForm>) => {
  bumProductSettingsFormRef.value = e;
};

const addBumProduct = () => {
  const productAlreadyAdded = store.bumProductList.filter((item) => {
    return item.priceId === bumpProduct.value.priceId;
  });
  if (productAlreadyAdded.length > 0) {
    sendNotification({
      title: t("common.payment.error.productAlreadyAdded"),
      type: "error",
    });
    return;
  }

  if (store.addedProductList.length < 1) {
    sendNotification({
      title: t("common.payment.error.addMainProductFirst"),
      type: "error",
    });
    return;
  }

  if (!props.currentProduct) {
    const priceProduct = product.value.prices.find(
      (price) => price._id === bumpProduct.value.priceId
    );
    if (!priceProduct) {
      sendNotification({
        title: t("common.payment.error.priceNotFoundForBumpProduct"),
        type: "error",
      });
      return;
    }
    const newBumProduct = {
      ...product.value,
      ...bumpProduct.value,
      amount: priceProduct.amount,
      type: PRODUCT_TYPE.BUMP_PRODUCT.value,
      currency: priceProduct.currency,
      compareAtPrice: priceProduct.compareAtPrice,
    };
    store.bumProductList.push(newBumProduct);
    if (store.bumProductList.length === 1) {
      store.payment.bumpSettings.enableBump = true;
    }
    store.anyUnsavedChanges = true;
  }
  emit("onBack");
};

const updateBumProductSettings = () => {
  store.bumProductList.forEach((storeProduct) => {
    if (storeProduct.priceId === bumpProduct.value.priceId) {
      storeProduct.headline = bumpProduct.value.headline;
      storeProduct.otoHeadline = bumpProduct.value.otoHeadline;
      storeProduct.otoText = bumpProduct.value.otoText;
      storeProduct.enableImage = bumpProduct.value.enableImage;
      storeProduct.uploadedImage = bumpProduct.value.uploadedImage;
      storeProduct.amount = bumpProduct.value.amount;
      storeProduct.currency = bumpProduct.value.currency;
      storeProduct.type = PRODUCT_TYPE.BUMP_PRODUCT.value;
    }
  });
  emit("onBack");
};

const addProduct = async () => {
  if (bumProductSettingsFormRef.value && product.value?.type === PRODUCT_TYPE.BUMP_PRODUCT.value) {
    const isValid = await bumProductSettingsFormRef.value.getForm().validate();
    if (!isValid) {
      return;
    }
  }
  if (!props.currentProduct && product.value?.type === PRODUCT_TYPE.BUMP_PRODUCT.value) {
    addBumProduct();
    return;
  } else if (props.currentProduct && product.value?.type === PRODUCT_TYPE.BUMP_PRODUCT.value) {
    updateBumProductSettings();
    return;
  }
  const productAlreadyAdded = store.addedProductList.filter((item) => {
    return item.productId === product.value.productId;
  });
  if (!props.currentProduct && productAlreadyAdded.length > 0) {
    sendNotification({
      title: t("common.payment.error.productAlreadyAdded"),
      type: "error",
    });
  }
  if (!props.currentProduct && product.value && productAlreadyAdded.length <= 0) {
    const productToAdd = {
      ...product.value,
      ...mainProductSettings.value,
      selectedPriceIds:
        (mainProductSettings.value.selectedPriceIds?.length ?? 0) > 0
          ? mainProductSettings.value.selectedPriceIds
          : getAllPriceIds(product.value?.prices),
    };
    if (store.addedProductList) {
      store.addedProductList.push(productToAdd);
    } else {
      store.addedProductList = [productToAdd];
    }
  }
  if (props.currentProduct) {
    store.addedProductList.forEach((storeProduct) => {
      if (storeProduct.productId === props.currentProduct.productId) {
        storeProduct.showDescription = mainProductSettings.value.showDescription;
        storeProduct.showImage = mainProductSettings.value.showImage;
        storeProduct.showQuantity = mainProductSettings.value.showQuantity;
        storeProduct.selectedPriceIds =
          (mainProductSettings.value.selectedPriceIds?.length ?? 0) > 0
            ? mainProductSettings.value.selectedPriceIds
            : getAllPriceIds(storeProduct.prices);
      }
    });
  }
  store.anyUnsavedChanges = true;
  emit("onBack");
};

const onDescriptionUpdate = (isDescriptionEnabled: boolean) => {
  mainProductSettings.value.showDescription = isDescriptionEnabled;
  store.anyUnsavedChanges = true;
};

const onImageUpdate = (isImageEnabled: boolean) => {
  mainProductSettings.value.showImage = isImageEnabled;
  store.anyUnsavedChanges = true;
};
const onQuantityUpdate = (isQuantityEnabled: boolean) => {
  mainProductSettings.value.showQuantity = isQuantityEnabled;
  store.anyUnsavedChanges = true;
};

const onPricingOptionsUpdate = (newValue: string[]) => {
  mainProductSettings.value.selectedPriceIds = newValue;
  store.anyUnsavedChanges = true;
};

const isMainProductDisabled = computed(() => !!props.currentProduct);

const isBumpDisabled = computed(
  () => !!props.currentProduct || store.addedProductList.length < 1
);

const updateProductTypeFromValue = (value: string) => {
  product.value.type = value;
  store.anyUnsavedChanges = true;
};

const bumpProduct = ref<BumProduct>();
const updateBumProduct = (bumProduct: BumProduct) => {
  bumpProduct.value = bumProduct;
  store.anyUnsavedChanges = true;
};

const onBack = () => {
  emit("onBack");
};
</script>

<style scoped>
a {
  text-decoration: underline !important;
  color: rgb(13, 110, 253) !important;
}

.border-button {
  margin-left: -1rem;
  margin-right: -1rem;
  border-top: 1px solid rgb(229 231 235);
  border-bottom: none;
  padding-top: 1rem;
}

.product-type-card {
  box-shadow: none;
}

.product-type-card:disabled {
  cursor: not-allowed !important;
  opacity: 0.65;
  border-color: #e4e7ec !important;
  background-color: #f9fafb !important;
}

.product-type-card:disabled:hover {
  border-color: #e4e7ec !important;
}

.product-type-card--selected:disabled {
  border-color: #b2ccff !important;
  background-color: #f5f8ff !important;
  opacity: 0.75;
}

.product-type-card:disabled img {
  opacity: 0.55;
}
</style>
